import React, { useState } from 'react';
import { Loader2, Moon } from 'lucide-react';
import Pagination from '../../Pagination';
import { MoonPhaseRow, UserProfile } from '../../../types';
import { illuminationToMoonFreezePerc } from '../../../utils/moonIcon';
import { getMoonPhaseName } from '../../../utils/moonPhaseName';
import MoonPhaseCodepenStatic from '../../MoonPhaseCodepenStatic';

interface Props {
    currentUser: UserProfile;
    rows: MoonPhaseRow[];
    totalCount: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    limit: number | 'all';
    onLimitChange: (newLimit: number | 'all') => void;
    filterFrom: string;
    filterTo: string;
    onFilterFromChange: (v: string) => void;
    onFilterToChange: (v: string) => void;
    onApplyFilters: () => void;
    onDataRefresh: () => void;
}

const MoonAllRecordsTab: React.FC<Props> = ({
    currentUser,
    rows,
    totalCount,
    currentPage,
    onPageChange,
    limit,
    onLimitChange,
    filterFrom,
    filterTo,
    onFilterFromChange,
    onFilterToChange,
    onApplyFilters,
    onDataRefresh,
}) => {
    const [genFrom, setGenFrom] = useState('');
    const [genTo, setGenTo] = useState('');
    const [loading, setLoading] = useState(false);

    const totalPages = limit === 'all' ? 1 : Math.ceil(totalCount / limit);

    const handleGenerate = async () => {
        if (!genFrom || !genTo) {
            alert('Wybierz zakres dat (od / do).');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch('/api/cms/moon-phase', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-admin-phone': currentUser.phone,
                },
                body: JSON.stringify({ date_from: genFrom, date_to: genTo }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                alert(data.error || 'Generowanie nie powiodło się.');
                return;
            }
            alert(`Zapisano: ${data.upserted ?? 0} rekordów.`);
            onDataRefresh();
        } catch (e) {
            console.error(e);
            alert('Błąd sieci.');
        } finally {
            setLoading(false);
        }
    };

    type ColumnKey = keyof MoonPhaseRow | 'phase' | 'img';

    const columns: { key: ColumnKey; label: string; width?: string }[] = [
        { key: 'id', label: 'id', width: '56px' },
        { key: 'date', label: 'date' },
        { key: 'img', label: 'IMG', width: '56px' },
        { key: 'phase', label: 'Phase', width: '180px' },
        { key: 'illumination', label: 'illumination' },
        { key: 'moon_age', label: 'moon_age' },
        { key: 'moonrise', label: 'moonrise' },
        { key: 'moonset', label: 'moonset' },
        { key: 'transit', label: 'transit' },
    ];

    return (
        <div className="w-full relative animate-fade-in space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Moon className="w-5 h-5" /> Moon — All records
                </h2>
                <div className="text-xs text-gray-500">
                    Central Poland - Warsaw · Europe/Warsaw
                </div>
            </div>

            <div className="bg-mystic-box p-4 rounded-lg border border-gray-800 space-y-3">
                <div className="text-xs uppercase text-gray-500 font-bold">Generator rekordów</div>
                <div className="flex flex-wrap items-end gap-4">
                    <div>
                        <label className="block text-[10px] text-gray-500 mb-1">date_from</label>
                        <input
                            type="date"
                            value={genFrom}
                            onChange={(e) => setGenFrom(e.target.value)}
                            className="bg-mystic-inset border border-gray-700 p-1.5 rounded text-white text-xs"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] text-gray-500 mb-1">date_to</label>
                        <input
                            type="date"
                            value={genTo}
                            onChange={(e) => setGenTo(e.target.value)}
                            className="bg-mystic-inset border border-gray-700 p-1.5 rounded text-white text-xs"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleGenerate}
                        disabled={loading}
                        className="bg-mystic-accent hover:bg-mystic-accentHover text-black font-bold px-4 py-2 rounded text-xs uppercase disabled:opacity-50 flex items-center gap-2"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Generuj rekordy
                    </button>
                </div>
            </div>

            <div className="space-y-4 bg-mystic-box p-4 rounded-lg border border-gray-800">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="flex flex-wrap items-end gap-4">
                        <div>
                            <label className="block text-[10px] text-gray-500 mb-1">Filtr: od</label>
                            <input
                                type="date"
                                value={filterFrom}
                                onChange={(e) => onFilterFromChange(e.target.value)}
                                className="bg-mystic-inset border border-gray-700 p-1.5 rounded text-white text-xs"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] text-gray-500 mb-1">Filtr: do</label>
                            <input
                                type="date"
                                value={filterTo}
                                onChange={(e) => onFilterToChange(e.target.value)}
                                className="bg-mystic-inset border border-gray-700 p-1.5 rounded text-white text-xs"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={onApplyFilters}
                            className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded text-xs font-bold uppercase"
                        >
                            Zastosuj filtr
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-400 font-bold">Na stronę:</label>
                        <select
                            value={limit}
                            onChange={(e) => onLimitChange(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                            className="bg-mystic-inset border border-gray-700 p-1.5 rounded text-white text-xs"
                        >
                            {[50, 100, 200, 500, 'all'].map((val) => (
                                <option key={val} value={val}>
                                    {val}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="text-xs text-gray-500">
                    Łącznie: {totalCount} · strona {currentPage} z {totalPages || 1}
                </div>
            </div>

            <div className="overflow-x-auto border border-gray-800 rounded-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-mystic-box">
                        <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase font-bold tracking-wider">
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className="p-2 border-r border-gray-800 last:border-r-0"
                                    style={{ width: col.width }}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800 bg-mystic-bg">
                        {rows.map((item) => {
                            const phaseLabel = getMoonPhaseName(item.moon_age, item.illumination);
                            return (
                            <tr key={item.id} className="hover:bg-mystic-btn">
                                {columns.map((col) => {
                                    if (col.key === 'img') {
                                        const freezePerc = illuminationToMoonFreezePerc(
                                            item.illumination,
                                            item.moon_age
                                        );
                                        return (
                                            <td
                                                key={col.key}
                                                className="p-1 border-r border-gray-800 last:border-r-0 align-middle w-14"
                                                title={`${phaseLabel} · freeze ${freezePerc}%`}
                                            >
                                                <div className="relative mx-auto h-10 w-10 overflow-hidden flex items-center justify-center">
                                                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 scale-[0.38] origin-center pointer-events-none">
                                                        <MoonPhaseCodepenStatic perc={freezePerc} />
                                                    </div>
                                                </div>
                                            </td>
                                        );
                                    }
                                    if (col.key === 'phase') {
                                        return (
                                            <td
                                                key={col.key}
                                                className="p-2 border-r border-gray-800 last:border-r-0 text-xs text-gray-200"
                                            >
                                                {phaseLabel}
                                            </td>
                                        );
                                    }
                                    const value = item[col.key];
                                    let display: React.ReactNode =
                                        value === null || value === undefined ? '—' : String(value);
                                    if (col.key === 'illumination' && typeof value === 'number') {
                                        display = value.toFixed(2);
                                    }
                                    if (col.key === 'moon_age' && typeof value === 'number') {
                                        display = value.toFixed(2);
                                    }
                                    return (
                                        <td
                                            key={col.key}
                                            className="p-2 border-r border-gray-800 last:border-r-0 text-xs text-gray-300"
                                        >
                                            {display}
                                        </td>
                                    );
                                })}
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && limit !== 'all' && (
                <div className="mt-4">
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
                </div>
            )}
        </div>
    );
};

export default MoonAllRecordsTab;
